/*
 * Copyright (c) 2024. Bolmate B.V.
 */

import React, {useState} from 'react';
import {exportUsers, selectUsers, UserSelectFilter} from '../../../util/requestSender';
import {ISO_DATE_FORMAT} from '../../../util/constants';
import {copyToClipboard, downloadFile, ResponseData} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import {SelectionUser} from '../../../util/interfaces';
import {onShowConfirmSnackbar} from '../../../util/renderUtil';
import UserSelection from './userSelection';
import {showDialog} from '@dvrd/dvr-controls';
import {useTranslation} from 'react-i18next';

type SelectParams = {page?: number; limit?: number};

export default function UserSelectionController() {
    const {t} = useTranslation();
    const [createdBefore, setCreatedBefore] = useState<IDate | null>(null);
    const [createdAfter, setCreatedAfter] = useState<IDate | null>(null);
    const [subStatus, setSubStatus] = useState<'active' | 'inactive' | null>(null);
    const [includeTrials, setIncludeTrials] = useState(true);
    const [includeFree, setIncludeFree] = useState(true);
    const [includeUnpaid, setIncludeUnpaid] = useState(true);
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState<Array<SelectionUser>>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(20);

    function onChangeCreatedBefore(value: IDate) {
        setCreatedBefore(value);
    }

    function onClearCreatedBefore() {
        setCreatedBefore(null);
    }

    function onChangeCreatedAfter(value: IDate) {
        setCreatedAfter(value);
    }

    function onClearCreatedAfter() {
        setCreatedAfter(null);
    }

    function onChangeSubStatus(value: 'active' | 'inactive' | '' | null) {
        if (value === '') value = null;
        setSubStatus(value);
    }

    function onChangeIncludeTrials() {
        setIncludeTrials(!includeTrials);
    }

    function onChangeIncludeFree() {
        setIncludeFree(!includeFree);
    }

    function onChangeIncludeUnpaid() {
        setIncludeUnpaid(!includeUnpaid);
    }

    function onChangePage(page: number) {
        return function () {
            setPage(page);
            onSelectUsers({page});
        };
    }

    function onChangeLimit(limit: number) {
        setLimit(limit);
        setPage(0);
        onSelectUsers({page: 0, limit});
    }

    function onApplyFilters() {
        onSelectUsers();
    }

    function onSelectUsers(params?: SelectParams) {
        setLoading(true);
        selectUsers({
            data: buildRequestFilters(params),
            callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    setUsers(data.users);
                    setTotal(data.total);
                } else
                    showDialog(
                        data.message ??
                            t(
                                'admin.userSelection.selectFailedMsg',
                                'Het selecteren van de gebruikers is niet gelukt.'
                            ),
                        t('admin.userSelection.selectFailedTitle', 'Selectie mislukt')
                    );
            }
        });
    }

    function onExportUsers() {
        if (!users.length)
            return showDialog(t('admin.userSelection.noUsersSelected', 'Er zijn geen gebruikers geselecteerd'));
        setLoading(true);
        exportUsers({
            data: buildRequestFilters(),
            callback: (data: ResponseData, response: Response) => {
                setLoading(false);
                if (data.status === 'success') {
                    // FILE RESPONSE
                    response.blob().then(blob => {
                        const header = response.headers.get('Content-Disposition');
                        let fileName = 'gebruikers.xlsx';
                        if (header)
                            fileName = header.substring(
                                header.indexOf('filename=') + 'filename='.length,
                                header.length
                            );
                        downloadFile(blob, fileName);
                    });
                } else
                    showDialog(
                        data.message ??
                            t(
                                'admin.userSelection.exportFailedMsg',
                                'Het exporteren van de gebruikers is niet gelukt.'
                            ),
                        t('admin.userSelection.exportFailedTitle', 'Exporteren mislukt')
                    );
            }
        });
    }

    function onCopyData(value?: string) {
        return function () {
            if (value)
                copyToClipboard(value).then(() => {
                    onShowConfirmSnackbar(
                        `'${value}' ${t('admin.userSelection.copiedToClipboard', 'gekopieerd naar klembord')}`
                    );
                });
        };
    }

    function buildRequestFilters(params?: SelectParams): UserSelectFilter {
        const _limit = params?.limit ?? limit;
        const _page = _limit === -1 ? 0 : (params?.page ?? page);
        return {
            created_after: createdAfter?.format(ISO_DATE_FORMAT) ?? null,
            created_before: createdBefore?.format(ISO_DATE_FORMAT) ?? null,
            subscription_status: subStatus,
            include_free: subStatus === 'active' && includeFree,
            include_trials: subStatus === 'active' && includeTrials,
            include_unpaid: subStatus === 'active' && includeUnpaid,
            subscription_started_after: null,
            subscription_started_before: null,
            page: _page,
            limit: _limit
        };
    }

    return (
        <UserSelection
            onChangeCreatedBefore={onChangeCreatedBefore}
            onClearCreatedBefore={onClearCreatedBefore}
            onChangeCreatedAfter={onChangeCreatedAfter}
            onClearCreatedAfter={onClearCreatedAfter}
            onChangeSubStatus={onChangeSubStatus}
            onChangeIncludeTrials={onChangeIncludeTrials}
            onChangeIncludeFree={onChangeIncludeFree}
            onChangeIncludeUnpaid={onChangeIncludeUnpaid}
            onChangePage={onChangePage}
            onChangeLimit={onChangeLimit}
            onApplyFilters={onApplyFilters}
            onExportUsers={onExportUsers}
            onCopyData={onCopyData}
            createdBefore={createdBefore}
            createdAfter={createdAfter}
            subStatus={subStatus}
            includeTrials={includeTrials}
            includeFree={includeFree}
            includeUnpaid={includeUnpaid}
            loading={loading}
            users={users}
            total={total}
            page={page}
            limit={limit}
        />
    );
}
